import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useGlobalContext } from '../../../../context/ContextGlobal'
import { deleteDocument, getDocuments } from '../../../../firebase/firestore'
import { GetPaginationCollection } from '../../../../firebase/functions'
import { Path } from '../../../../router/paths'
import { TryCatch } from '../../../../utils/general'

const normalizeStr = (str) =>
  str
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') ?? ''

const CACHE_KEY = {
  page: 'adminCollaborators_page', // localStorage — persiste entre sessões
  status: 'adminCollaborators_status', // sessionStorage — persiste na sessão
  stateFilter: 'adminCollaborators_state', // sessionStorage — persiste na sessão
  order: 'adminCollaborators_order', // sessionStorage — persiste na sessão
}

const DEFAULT_FILTERS = {
  status: { status: '1' }, // Em Analise
  stateFilter: '', // Todos os estados
  order: 'desc', // Mais recentes primeiro
  page: 1,
}

export const useAdminCollaborators = () => {
  const { setAlerts, dataContext, setDataContext, setInfosContext, loading, setLoading } = useGlobalContext()

  const [search, setSearch] = useState('')
  const [searchedTerm, setSearchedTerm] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()

  // Filtros — sessionStorage: restaura ao voltar da edição, reseta ao abrir nova sessão
  const [status, setStatus] = useState(() => {
    const cached = sessionStorage.getItem(CACHE_KEY.status)
    return cached ? JSON.parse(cached) : DEFAULT_FILTERS.status
  })
  const [stateFilter, setStateFilter] = useState(
    () => sessionStorage.getItem(CACHE_KEY.stateFilter) || DEFAULT_FILTERS.stateFilter,
  )
  const [order, setOrder] = useState(() => sessionStorage.getItem(CACHE_KEY.order) || DEFAULT_FILTERS.order)

  // Página — localStorage: persiste entre sessões para voltar ao mesmo lugar
  const [paginationValue, setPaginationValue] = useState(() => {
    const cached = localStorage.getItem(CACHE_KEY.page)
    return cached ? Number(cached) : DEFAULT_FILTERS.page
  })

  // Dados vindos do Firestore, com filtro client-side para buscas multi-palavra.
  // Usa `searchedTerm` (o que foi efetivamente buscado) e não `search` (o que o usuário está digitando),
  // evitando filtrar dados antigos enquanto o usuário ainda está digitando.
  const sortedData = useMemo(() => {
    const data = dataContext?.colaboradores?.data ?? []
    if (!searchedTerm) return data
    const words = normalizeStr(searchedTerm).trim().split(' ').filter(Boolean)
    if (words.length <= 1) return data
    return data.filter((collab) => words.every((word) => normalizeStr(collab.name).includes(word)))
  }, [dataContext?.colaboradores?.data, searchedTerm])

  // ─── Busca no Firestore ────────────────────────────────────────────────────

  const getDatas = async (searchValue) => {
    // aceita searchValue como parâmetro para usar o valor mais recente
    const currentSearch = searchValue !== undefined ? searchValue : search

    if (location.pathname === Path.AdminCollaborators) {
      setSearchParams({ page: paginationValue, search: currentSearch })
    }

    const collectionName = 'colaboradores'
    const statusValue = Number(status?.status) || 0
    // "Em Criação" (status 0) inclui cadastros que nunca receberam `ts_created` (só passou a ser
    // gravado depois); Firestore exclui do orderBy documentos sem o campo, então usamos `dt_created`
    // (presente em todo documento) só para esse filtro
    const isCreationStatus = statusValue === 0
    const orderBy = isCreationStatus ? 'dt_created' : 'ts_created'
    const limitResultPerPage = 25
    const querys = []

    if (currentSearch !== '') {
      querys.push({
        field: 'search',
        operator: 'array-contains',
        value: normalizeStr(currentSearch).trim().split(' ')[0],
      })
    }

    querys.push({ field: 'status', operator: '==', value: statusValue })
    if (stateFilter !== '') {
      querys.push({ field: 'address.state', operator: '==', value: stateFilter })
    }

    const result = await GetPaginationCollection(
      paginationValue,
      limitResultPerPage,
      collectionName,
      orderBy,
      querys,
      order,
    )

    setSearchedTerm(currentSearch)
    return setDataContext({ colaboradores: result })
  }

  // ─── Navegação de página ──────────────────────────────────────────────────

  const handleSetPaginationValue = (value) => {
    localStorage.setItem(CACHE_KEY.page, value)
    setPaginationValue(value)
  }

  const nextPage = () => {
    const currentPage = dataContext?.colaboradores?.pagination?.currentPage
    handleSetPaginationValue(currentPage + 1)
  }

  const previousPage = () => {
    const currentPage = dataContext?.colaboradores?.pagination?.currentPage
    handleSetPaginationValue(currentPage - 1)
  }

  // ─── Setters com sessionStorage ───────────────────────────────────────────

  const handleSetStatus = (value) => {
    sessionStorage.setItem(CACHE_KEY.status, JSON.stringify(value))
    handleSetPaginationValue(1)
    setStatus(value)
  }

  const handleSetStateFilter = (value) => {
    sessionStorage.setItem(CACHE_KEY.stateFilter, value)
    handleSetPaginationValue(1)
    setStateFilter(value)
  }

  const handleSetOrder = (value) => {
    sessionStorage.setItem(CACHE_KEY.order, value)
    setOrder(value)
  }

  const handleSetSearch = (value) => {
    setSearch(value)
  }

  // ─── Infos adicionais ─────────────────────────────────────────────────────

  const getInfos = async () => {
    const getPositions = await getDocuments('cargos')
    setInfosContext({ cargos: getPositions })
  }

  const FetchGetDatas = async () => {
    // ao submeter o form, volta para página 1 se tiver search ativo
    if (search !== '' && paginationValue !== 1) {
      handleSetPaginationValue(1)
    }
    await TryCatch(getDatas, setLoading, setAlerts)
  }

  const FetchGetInfos = async () => {
    await TryCatch(getInfos, setLoading, setAlerts)
  }

  // ─── Deletar colaborador ──────────────────────────────────────────────────

  const handleDeleteCollaborator = async (id) => {
    const collaborator = dataContext?.colaboradores?.data?.find((c) => c.id === id)

    if (!collaborator) {
      return setAlerts((prev) => [
        ...prev,
        { title: 'Id inválido', msg: `O colaborador com id ${id} não existe.`, type: 'error' },
      ])
    }

    await deleteDocument('colaboradores', id)

    setAlerts((prev) => [
      ...prev,
      { title: 'Colaborador deletado', msg: `${collaborator.nome} foi deletado com sucesso.`, type: 'success' },
    ])

    await FetchGetDatas()
  }

  // ─── Effects ──────────────────────────────────────────────────────────────

  // Busca nova quando muda status, stateFilter, página ou order
  // search só é aplicado via submit do formulário (Enter ou botão)
  useEffect(() => {
    FetchGetDatas()
    FetchGetInfos()
  }, [paginationValue, status, stateFilter, order])

  // ─── Return ───────────────────────────────────────────────────────────────

  return {
    loading,
    search,
    setSearch: handleSetSearch,
    dataContext: {
      ...dataContext,
      colaboradores: {
        ...dataContext?.colaboradores,
        data: sortedData,
      },
    },
    paginationValue,
    setPaginationValue: handleSetPaginationValue,
    nextPage,
    previousPage,
    handleDeleteCollaborator,
    searchParams,
    FetchGetDatas,
    FetchGetInfos,
    status,
    setStatus: handleSetStatus,
    order,
    setOrder: handleSetOrder,
    stateFilter,
    setStateFilter: handleSetStateFilter,
  }
}
